import { DatePicker } from "@mui/x-date-pickers";
import { useContext, useEffect, useState } from "react";
import { CustomFormContext } from "../../CustomForm";
import {
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { prepareContent } from "../../helpers";
import { ErrorInfo, Validation } from "../../types";
import { useTranslation } from "react-i18next";
import { FieldTypeId } from "../../../../lib/enums";

interface LocalizedDatePickerProps {
  nonForm?: boolean;
  validations?: Validation[];
  fieldId: number;
  alias: string;
  header: string;
  typeId: FieldTypeId;
  localized?: boolean;
  readOnly?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

const CustomDatePicker = ({
  nonForm = false,
  fieldId,
  alias,
  header,
  typeId,
  localized = false,
  readOnly = false,
  required = false,
  placeholder = "",
  className = "",
  style = {},
}: LocalizedDatePickerProps) => {
  const {
    fieldValue,
    getValidationErrors,
    setDataDispatch,
    dataIsSet,
    errorsAreSet,
    tempData,
    showErrors,
  } = useContext(CustomFormContext);
  const { t } = useTranslation();
  const [dateValue, setDateValue] = useState<string | null>(null);
  const [errors, setErrors] = useState<ErrorInfo[] | null>(null);

  useEffect(() => {
    if (!dataIsSet || !errorsAreSet) {
      return;
    }
    const data = fieldValue(alias);

    if (data === undefined) {
      return;
    }

    setDateValue(data);

    const errors = getValidationErrors(alias);
    if (errors) {
      setErrors(errors);
    }
  }, [dataIsSet, errorsAreSet, tempData]);

  useEffect(() => {
    setErrors(getValidationErrors(alias));
  }, [getValidationErrors, alias]);

  const handleOnChange = (newValue: Dayjs | null) => {
    const formattedDate = newValue ? newValue.format("YYYY-MM-DD") : null;
    const data = prepareContent(formattedDate, alias, nonForm);
    setDataDispatch({
      type: "UPDATE_CONTENT",
      payload: data,
    });
  };

  return (
    <Grid item xs={12} sm={6} lg={4} className={className} style={style}>
      {header && (
        <InputLabel htmlFor={alias}>
          {header}{" "}
          {(required || errors?.find((a) => a.error === "isNotNullOrEmpty")) &&
            "*"}{" "}
          {localized && (
            <Chip size="small" label={t("customform.localized")} color="info" />
          )}
        </InputLabel>
      )}
      <FormControl style={{ width: "100%" }}>
        <DatePicker
          label={placeholder}
          value={dayjs(dateValue)}
          disabled={
            errors && errors.find((a) => a.error === "isDisabledIfNew")
              ? true
              : false
          }
          sx={{ minWidth: 150 }}
          format={"YYYY-MM-DD"}
          onChange={handleOnChange}
          readOnly={readOnly}
          data-test={alias}
          closeOnSelect
          slotProps={{
            textField: {
              error: false,
            },
            actionBar: {
              actions: ["today", "clear", "accept"],
              sx: {
                justifyContent: "space-between",
              },
            },
          }}
          localeText={{
            todayButtonLabel: "Idag",
            clearButtonLabel: "Rensa",
            okButtonLabel: "Stäng",
          }}
        />
      </FormControl>
      {showErrors &&
        errors
          ?.filter((a) => a.error !== "isDisabledIfNew")
          .map((info, index) => (
            <FormHelperText key={index} variant="standard">
              <Typography variant="caption" color="red">
                {info.message}
              </Typography>
            </FormHelperText>
          ))}
    </Grid>
  );
};

CustomDatePicker.fieldType = "CustomDatePicker";

export default CustomDatePicker;
